<template lang="pug">
v-container.fill-height.align-start.flex-column(fluid)
    BoarderGroupEditorDialog(
        v-if="showEditorDialog" :show="showEditorDialog" :boarderGroup="currentBoarderGroup"
        @close="onBoarderGroupEditorClose" @boarderGroupSave="onBoarderGroupEditorSave"
        :priceAlgorithmId="priceAlgorithmId"
    )

    v-container.pa-0
        v-toolbar(dense flat)
            v-btn(small text :disabled="!isEditButtonEnabled" @click="onEditGroupButtonClick")
                v-icon fas fa-edit
                span.ma-2 {{$t('settingsPage.boarderGroups.editGroupButtonLabel')}}
    v-container.pa-0.flex-shrink-0.flex-grow-1
        AgGridVue.mt-2.ag-theme-balham(
            style="width: 100%; height: 100%; min-height: 500px"
            :gridOptions="gridOptions"
            :columnDefs="columnDefs"
            :rowData="boarderGroupList"
            @grid-ready="onGridReady"
            @selection-changed="onSelectionChange"
            @row-double-clicked="onGroupDetailEvent"
            @rowDataChanged="onRowDataChanged"
        )

</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import {Component} from 'vue-property-decorator'
import {Action} from 'vuex-class'
import {AgGridVue} from 'ag-grid-vue'
import {BOARDER_FETCH_BOARDER_GROUP_LIST_AS_INTERFACE} from '@/store/action-types'
import {BoarderGroupResponseModel} from '@/model/boarder/BoarderGroup'
import {TagResponseItem} from '@/types/service-response'
import {ColDef, GridApi, GridOptions, ICellRendererParams, RowClickedEvent} from 'ag-grid-community'
import BoarderGroupEditorDialog from '@/components/settings/BoarderGroupEditorDialog.vue'

@Component({
  components: {
    BoarderGroupEditorDialog,
    AgGridVue
  }
})
export default class BoarderGroupPage extends Vue {
  private boarderGroupList: BoarderGroupResponseModel[] = []
  private showEditorDialog: boolean = false
  private currentBoarderGroup: BoarderGroupResponseModel | null = null
  private currentSelection: BoarderGroupResponseModel[] = []
  private gridOptions!: GridOptions
  private columnDefs: ColDef[] = []
  private priceAlgorithmId: number | null = null
  private gridApi:GridApi | null = null
  private newBoarderGroup: BoarderGroupResponseModel | null = null

  @Action(BOARDER_FETCH_BOARDER_GROUP_LIST_AS_INTERFACE) fetchBoarderGroups!: ({ (): Promise<BoarderGroupResponseModel[]> })

  get isEditButtonEnabled(): boolean {
    return this.currentSelection.length > 0
  }

  onGridReady(params: ICellRendererParams) {
    this.gridApi = params.api;
  }

  onSelectionChange(params: GridOptions): void {
    this.currentSelection = params.api!.getSelectedRows()
    this.priceAlgorithmId = this.currentSelection[0].priceAlgorithm ? this.currentSelection[0].priceAlgorithm.id : null
  }

  onGroupDetailEvent(params: RowClickedEvent) {
    this.currentBoarderGroup = params.data
    this.priceAlgorithmId = this.currentSelection[0].priceAlgorithm ? this.currentSelection[0].priceAlgorithm.id : null
    this.showEditorDialog = true
  }

  onEditGroupButtonClick(): void {
    this.currentBoarderGroup = _.first(this.currentSelection) || null
    this.priceAlgorithmId = this.currentSelection[0].priceAlgorithm ? this.currentSelection[0].priceAlgorithm.id : null
    this.showEditorDialog = true
  }

  onBoarderGroupEditorClose(): void {
    this.currentBoarderGroup = null
    this.priceAlgorithmId = null
    this.showEditorDialog = false
  }

  onBoarderGroupEditorSave(result: BoarderGroupResponseModel): void {
    this.newBoarderGroup = result
    this.currentBoarderGroup = null
    this.priceAlgorithmId = null
    this.showEditorDialog = false
    this.fetchData()
  }

  onRowDataChanged (): void {
    if(this.newBoarderGroup) {
    this.onFlashRow()
    }
  }

  async fetchData (): Promise<void> {
    this.gridOptions.api?.showLoadingOverlay()
    try {
      this.boarderGroupList = await this.fetchBoarderGroups()
    } catch (e) {
    } finally {
      this.gridOptions.api?.hideOverlay()
    }
  }

  createGridOptions(): GridOptions {
    return {
      rowSelection: 'single',
      suppressContextMenu: true,

      defaultColDef: {
        sortable: true,

        filter: 'agTextColumnFilter',

        filterParams: {
          debounceMs: 250,
          textFormatter: (value: string) => value.normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
        },

        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        }
      },
      overlayLoadingTemplate:
      `<div class="ag-overlay-loading-center" style="font-size: 15px">
           <i class="fas fa-spinner fa-pulse"></i>
           <span style="padding-left: 10px"> ${ this.$t('boarderGroupEditorDialog.gridLoader') } </span>
       </div>`,
    }
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        colId: 'groupName',
        flex: 1,
        headerName: this.$t('settingsPage.boarderGroups.groupColumnHeaderLabel').toString(),
        field: 'name',
      },
      {
        colId: 'customerName',
        flex: 1,
        headerName: this.$t('settingsPage.boarderGroups.customerColumnHeaderLabel').toString(),
        field: 'businessCustomer.customer.name',
      },
      {
        colId: 'tags',
        flex: 1,
        headerName: this.$t('settingsPage.boarderGroups.tagsColumnHeaderLabel').toString(),
        valueGetter: (params) => (params.data?.tags || []).map((tag: TagResponseItem) => tag.title).join(', '),
      },
    ]
  }

  onFlashRow() {
    const foundBoarderGroup: BoarderGroupResponseModel | undefined = this.boarderGroupList.find((item: BoarderGroupResponseModel) => item.id === this.newBoarderGroup!.id)
    if(foundBoarderGroup) {
      const index: number = this.boarderGroupList.indexOf(foundBoarderGroup)
      this.onFlashRows([index])
    }
    this.newBoarderGroup = null
  }

  onFlashRows(indexList: number[]) {
    this.gridOptions.api?.ensureIndexVisible(indexList[0])
    let rowNode: any[] = []
    indexList.forEach((item: number) => {
      rowNode.push(this.gridApi!.getDisplayedRowAtIndex(item))
    })
    this.gridApi?.flashCells({
      rowNodes: rowNode,
    });
  }

  created(): void {
    this.gridOptions = this.createGridOptions()
    this.columnDefs = this.createColumnDefs()
  }

  mounted() {
    this.fetchData()
  }
}
</script>

<style lang="stylus" scoped>

.ag-theme-balham
  >>> .ag-cell-data-changed
    background-color yellow !important


</style>
